import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Activity, Loader2 } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import * as api from "@/lib/api";
import { ApiError } from "@/lib/api";
import { queryKeys, useAuthStatus } from "@/lib/queries";

function SetupShell({ children }: { children: ReactNode }) {
  return (
    <main className="relative grid min-h-dvh place-items-center overflow-hidden bg-background px-4 py-10 text-foreground">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-48 -top-48 size-96 rounded-full bg-status-up/10 blur-3xl" />
        <div className="absolute -bottom-48 -right-48 size-96 rounded-full bg-status-up/10 blur-3xl" />
      </div>
      {children}
    </main>
  );
}

function Brand() {
  return (
    <div className="mb-2 flex items-center gap-2.5">
      <div className="grid size-9 place-items-center rounded-lg bg-status-up/15">
        <Activity className="size-5 text-status-up" />
      </div>
      <span className="text-lg font-semibold tracking-tight">Vigil</span>
    </div>
  );
}

function SetupLoading() {
  return (
    <SetupShell>
      <Card className="relative w-full max-w-sm shadow-xl shadow-black/5">
        <CardHeader className="space-y-3">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    </SetupShell>
  );
}

export default function SetupPage() {
  const auth = useAuthStatus();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.setup(username, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth });
      navigate("/dashboard", { replace: true });
    },
  });

  if (auth.isLoading) return <SetupLoading />;
  if (auth.data?.authenticated) return <Navigate to="/dashboard" replace />;
  if (auth.data && !auth.data.needsSetup) return <Navigate to="/login" replace />;

  function clearError() {
    if (validationError) setValidationError(null);
    if (mutation.error) mutation.reset();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password.length < 8) {
      setValidationError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }
    setValidationError(null);
    mutation.mutate();
  }

  const requestError = mutation.error instanceof ApiError ? mutation.error.message : mutation.error?.message;
  const error = validationError ?? requestError;

  return (
    <SetupShell>
      <Card className="relative w-full max-w-sm shadow-xl shadow-black/5">
        <CardHeader>
          <Brand />
          <CardTitle className="text-xl">Create your admin account</CardTitle>
          <CardDescription>This instance has no users yet.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  clearError();
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearError();
                }}
                aria-invalid={Boolean(error)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  clearError();
                }}
                aria-invalid={Boolean(error)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button className="w-full" type="submit" disabled={!username || mutation.isPending}>
              {mutation.isPending && <Loader2 className="animate-spin" />}
              {mutation.isPending ? "Creating account…" : "Create account"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </SetupShell>
  );
}
